/**
 * Muntons (Stowmarket, Suffolk) — the yeast the maltster sells to home brewers
 * in its own sachets: the two beer-kit strains bought loose, and the whole
 * Gervin range.
 *
 * Muntons is a maltster first, and it publishes for its yeast roughly nothing.
 * The thirteen Gervin sachets each have a page of their own at muntons.com
 * (fetched in September 2026, and linked here), but every one of those pages
 * prints a single sentence of character and then an empty specification table:
 * product code, weight, volume and ABV are all rendered as "-". Attenuation,
 * flocculation and alcohol tolerance are published nowhere on the site, in the
 * kit instruction PDFs, or in the homebrew recipe designer. So every entry in
 * this file is `representative`, and each description says which figure is
 * ours rather than Muntons'.
 *
 * Three temperatures here are genuinely published, and only three: GV8 "ferments
 * between 18-30°C", GV6 "ferments down to 5°C" and GV5 "Ferments down to 8°C".
 * The floors of GV5 and GV6 are therefore real and their ceilings are ours.
 * Every other Gervin band is class-typical for the job the sentence describes.
 *
 * The two loose kit yeasts — Standard Active Dried and Premium Gold — have no
 * page anywhere on muntons.com; they are sold by shops from bulk and by Muntons
 * only inside kits, so both are entered without a `url` rather than pointing at
 * a retailer. Their temperature bands are the ones Muntons' own brewing
 * instructions give for the ranges those sachets ship in: 18–21 °C for the
 * Premium Range (standard yeast) and 18–20 °C for the Premium Gold range.
 *
 * No `dosageGramsPerHl` anywhere. Muntons publishes no g/hL or g/L rate; its
 * recipe designer counts whole sachets instead (one per 40 UK pints, two from
 * 50 pints up), and a sachet count is not a rate — the sachets in question run
 * from 6 g to 11 g. `pitchPlan`'s class-typical fallback is the honest answer
 * here. `alcoholTolerancePercent` and `sta1` are `null` throughout for the same
 * reason: Muntons publishes neither, not even for the strain it sells as its
 * "high alcohol" wine yeast.
 *
 * Type is `ale` throughout, including the wine and cider strains, following the
 * convention the Lalvin and Red Star files set.
 */

import type { Yeast } from '../../schema/yeast';

/** Muntons and Gervin yeast. @see Yeast */
export const muntonsYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // The kit sachets, sold loose
  // -------------------------------------------------------------------------
  {
    id: 'muntons-standard',
    lab: 'Muntons',
    productCode: 'Standard',
    name: 'Muntons Standard Active Dried Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The plain sachet that ships in most Muntons kits and is sold loose by the shops that stock them: an unfussy English ale strain that starts fast when sprinkled dry, finishes short of dry and drops a firm cake. The 18–21 °C band is the one Muntons prints in its Premium Range brewing instructions; attenuation and flocculation are class-typical rather than published, because Muntons publishes neither.',
    tags: [
      'english-ale',
      'kit-yeast',
      'bitter',
      'mild',
      'brown-ale',
      'neutral',
      'workhorse',
      'clearing',
    ],
    provenance: 'representative',
  },
  {
    id: 'muntons-premium-gold',
    lab: 'Muntons',
    productCode: 'Premium Gold',
    name: 'Muntons Premium Gold',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The better sachet in the Muntons range, and the one the Gold kits ship with: Muntons sells it on its yield in all-malt worts, and its own FAQ recommends rehydrating this yeast rather than sprinkling it, because a sluggish start is the fault it is asked about most. The 18–20 °C band comes from the Premium Gold brewing instructions; attenuation and flocculation are ours, as Muntons prints no figure for either.',
    tags: [
      'english-ale',
      'kit-yeast',
      'all-malt',
      'bitter',
      'golden-ale',
      'porter',
      'rehydrate',
      'clearing',
    ],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Gervin — Muntons' own sachet range
  // -------------------------------------------------------------------------
  {
    id: 'muntons-gv1',
    lab: 'Muntons',
    productCode: 'GV1',
    name: 'Gervin GV1 Universal Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The green-label Gervin sachet and the default of the range: a general-purpose wine strain that starts quickly, settles hard and leaves the bouquet alone. Reach for it when the must has no particular demands. Everything numeric here is ours — Muntons publishes no attenuation, temperature, flocculation or alcohol tolerance for any Gervin strain — so the band assumes it ferments a wine out.',
    tags: [
      'wine',
      'gervin',
      'green-label',
      'neutral',
      'dry-finish',
      'fast',
      'clearing',
      'country-wine',
    ],
    url: 'https://www.muntons.com/home_brewing/gv1-green-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv2',
    lab: 'Muntons',
    productCode: 'GV2',
    name: 'Gervin GV2 Robust Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Red label: the quick, low-foam strain Muntons points at full-bodied reds and whites, useful when headspace is tight and a vigorous ferment would otherwise climb out. Attenuation, temperature and flocculation are class-typical for a dry-finishing wine yeast, not published figures.',
    tags: [
      'wine',
      'gervin',
      'red-label',
      'red-wine',
      'white-wine',
      'full-bodied',
      'low-foam',
      'fast',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv2-red-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv3',
    lab: 'Muntons',
    productCode: 'GV3',
    name: 'Gervin GV3 Sparkling Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Yellow label, and the one Muntons sends at stronger sparkling wines and higher-alcohol dessert wines — which is the description of a champagne-type strain, and the sachet a brewer would pick off this range for bottle-conditioning something big. Muntons prints no alcohol tolerance, so it is null here, and the attenuation and temperature bands are ours.',
    tags: [
      'wine',
      'gervin',
      'yellow-label',
      'sparkling',
      'champagne',
      'dessert-wine',
      'bottle-conditioning',
      'high-gravity',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv3-yellow-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv4',
    lab: 'Muntons',
    productCode: 'GV4',
    name: 'Gervin GV4 High Alcohol Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Purple label: low foam and very tolerant, in the only two respects Muntons describes it — the range's pick for a heavy must, a strong mead or a barleywine that has to go the distance. How tolerant, in per cent, is not published anywhere, so alcohol tolerance stays null rather than being guessed at, and the rest of the numbers are class-typical.",
    tags: [
      'wine',
      'gervin',
      'purple-label',
      'high-gravity',
      'high-alcohol',
      'mead',
      'low-foam',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv4-purple-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv5',
    lab: 'Muntons',
    productCode: 'GV5',
    name: 'Gervin GV5 White Fruit Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 93, max: 100 },
    temperatureC: { min: 8, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Gray label, aimed at white fruit wines — apple, pear, gooseberry, elderflower — and one of the two Gervin strains Muntons gives a real number for: it ferments down to 8 °C, which is what makes it worth having for a cool, slow, aroma-preserving ferment. The 8 °C floor is published; the ceiling, the attenuation band and the flocculation are ours.',
    tags: [
      'wine',
      'gervin',
      'grey-label',
      'white-wine',
      'fruit-wine',
      'cool-ferment',
      'aromatic',
      'cider',
    ],
    url: 'https://www.muntons.com/home_brewing/gv5-grey-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv6',
    lab: 'Muntons',
    productCode: 'GV6',
    name: 'Gervin GV6 Light Dessert Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 95 },
    temperatureC: { min: 5, max: 30 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Orange label, and the most interesting sachet in the range: a Sauternes strain that Muntons says ferments down to 5 °C and gives fruity, floral character. That 5 °C floor is published and is the lowest of any Gervin. The attenuation band is deliberately short of dry, because a light dessert wine is meant to keep sugar, but Muntons prints no figure — it, the ceiling and the flocculation are ours.',
    tags: [
      'wine',
      'gervin',
      'orange-label',
      'dessert-wine',
      'sauternes',
      'fruity',
      'floral',
      'cool-ferment',
      'residual-sweetness',
    ],
    url: 'https://www.muntons.com/home_brewing/gv6-orange-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv7',
    lab: 'Muntons',
    productCode: 'GV7',
    name: 'Gervin GV7 Restart Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Brown label, sold for one job: getting a stuck ferment moving again when there is still sugar and already alcohol. A restart strain is chosen for exactly the tolerance Muntons declines to quantify, so alcohol tolerance is null here; the attenuation, temperature and flocculation figures are class-typical rather than published.',
    tags: [
      'wine',
      'gervin',
      'brown-label',
      'stuck-fermentation',
      'restart',
      'mead',
      'cider',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv7-brown-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv8',
    lab: 'Muntons',
    productCode: 'GV8',
    name: 'Gervin GV8 Red Table Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 18, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Burgundy label, pointed at Bordeaux and claret styles, and the only Gervin strain with a full temperature range on its page: Muntons publishes 18–30 °C, which is warm enough to pull color and tannin out of a red must. Attenuation and flocculation are ours; Muntons prints neither.',
    tags: [
      'wine',
      'gervin',
      'burgundy-label',
      'red-wine',
      'table-wine',
      'bordeaux',
      'structure',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv8-burgundy-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv9',
    lab: 'Muntons',
    productCode: 'GV9',
    name: 'Gervin GV9 White Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 93, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Black label, sold for German-style whites and, Muntons adds, for rhubarb and gooseberry wines — the sharp, aromatic country musts where a strain that keeps acidity and aroma matters more than one that builds body. No attenuation, temperature or flocculation is published, so all three are class-typical here.',
    tags: [
      'wine',
      'gervin',
      'black-label',
      'white-wine',
      'german',
      'country-wine',
      'rhubarb',
      'aromatic',
    ],
    url: 'https://www.muntons.com/home_brewing/gv9-black-labe/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv10',
    lab: 'Muntons',
    productCode: 'GV10',
    name: 'Gervin GV10 Light Sparkling Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Gold label, and the lighter of the two sparkling strains: Muntons puts it on superior sparkling and rose wines, where GV3 is the one for stronger stuff. Nothing numeric is published for it, so the attenuation, temperature and flocculation given here are class-typical for a sparkling wine yeast.',
    tags: [
      'wine',
      'gervin',
      'gold-label',
      'sparkling',
      'rose',
      'white-wine',
      'bottle-conditioning',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv10-gold-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv11',
    lab: 'Muntons',
    productCode: 'GV11',
    name: 'Gervin GV11 Red Fruit Wine Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Dark red label, the fruit-wine counterpart to GV8: Muntons sells it for red fruit musts — blackberry, damson, elderberry — and for the bouquet it leaves behind, which is the argument for using it over a neutral universal strain in a melomel too. Attenuation, temperature and flocculation are ours, as none is published.',
    tags: [
      'wine',
      'gervin',
      'dark-red-label',
      'fruit-wine',
      'red-wine',
      'melomel',
      'aromatic',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv11-dark-red-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv12',
    lab: 'Muntons',
    productCode: 'GV12',
    name: 'Gervin GV12 Ale Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Tan label, and the only beer strain in the Gervin range: an English ale yeast Muntons sells on a rapid, clean ferment across most recipes, in an 11 g sachet rather than the 6 g the kit yeasts come in. It is the obvious step up from the sachet taped to a kit lid. Muntons publishes no attenuation, flocculation, temperature or alcohol tolerance for it, so the first three here are class-typical English ale figures and the fourth is null.',
    tags: [
      'english-ale',
      'gervin',
      'tan-label',
      'bitter',
      'pale-ale',
      'porter',
      'fast',
      'clearing',
      'workhorse',
    ],
    url: 'https://www.muntons.com/home_brewing/gv12-tan-label/',
    provenance: 'representative',
  },
  {
    id: 'muntons-gv13',
    lab: 'Muntons',
    productCode: 'GV13',
    name: 'Gervin GV13 Cider Yeast',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Dark green label: the cider sachet, sold for a fast, clean ferment on any juice and nothing more specific than that. Cider left to this strain finishes dry and wants back-sweetening if you do not want it bone dry. Muntons publishes no numbers at all for it, so attenuation, temperature and flocculation are all class-typical for a cider yeast here.',
    tags: [
      'cider',
      'gervin',
      'dark-green-label',
      'perry',
      'apple',
      'neutral',
      'fast',
      'dry-finish',
    ],
    url: 'https://www.muntons.com/home_brewing/gv13-dark-green-label/',
    provenance: 'representative',
  },
];
